import {
  useCallback,
  type Dispatch,
  type MouseEvent as ReactMouseEvent,
  type MutableRefObject,
  type SetStateAction,
} from "react";

import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import type { DocumentViewportValue } from "@/features/document-viewport/types";
import type { MeasurementScale } from "@/hooks/useMeasurementScale";
import type { Point } from "@/lib/geometry";
import { TOOL_IDS, type MeasurementType, type ToolType } from "@/viewport-tools/measurement";

import { commitPreviewMeasurementUpdates } from "./commit-measurement-updates";
import {
  finalizeDrawingInteraction,
  handleDrawingPointerDownInteraction,
  updateDrawingPreviewInteraction,
} from "./drawing-interactions";
import { applyTransformPreviewOnPointerMove } from "./transform-interactions";
import type { InteractionState, MeasurementOverlayContextMenuState } from "./types";
import type {
  DraftMeasurementState,
  MeasurementAnnotation,
  MeasurementGeometry,
  MeasurementOverlayRecord,
  PolygonGeometry,
} from "../measurement-overlay-types";

interface UseMeasurementOverlayStageHandlersArgs {
  viewport: DocumentViewportValue;
  scale: MeasurementScale | null;
  activeTool: ToolType | null;
  activeMeasurementType: MeasurementType | null;
  snapEnabled: boolean;
  incrementSnapEnabled: boolean;
  measurements: MeasurementOverlayRecord[];
  selectedMeasurementIds: Id<"takeoffPilotMeasurements">[];
  previewAnnotations: Record<string, MeasurementAnnotation>;
  previewGeometries: Record<string, MeasurementGeometry | PolygonGeometry>;
  interaction: InteractionState | null;
  interactionRef: MutableRefObject<InteractionState | null>;
  drawingDraftRef: MutableRefObject<DraftMeasurementState | null>;
  latestDraftRef: MutableRefObject<DraftMeasurementState | null>;
  lastPointerPagePointRef: MutableRefObject<Point | null>;
  isSpacePressedRef: MutableRefObject<boolean>;
  getPagePoint: (clientX: number, clientY: number) => Point | null;
  getScreenDistance: (pointA: Point, pointB: Point) => number;
  getActiveHeight: () => number | undefined;
  snapSegmentPoint: (anchor: Point, candidate: Point) => Point;
  snapRectangleCorner: (
    anchor: Point,
    candidate: Point,
    lockSquare: boolean
  ) => Point;
  updateUnsavedDraft: (draft: DraftMeasurementState) => void;
  onDraftMeasurementChange: (draft: DraftMeasurementState | null) => void;
  onSelectMeasurement: (
    measurementId: Id<"takeoffPilotMeasurements"> | null,
    options?: { shiftKey?: boolean }
  ) => void;
  onUpdateMeasurement: (
    measurementId: Id<"takeoffPilotMeasurements">,
    geometry: MeasurementGeometry | PolygonGeometry,
    height: number | undefined,
    value: number
  ) => Promise<void>;
  onUpdateMeasurementAnnotation?: (
    measurementId: Id<"takeoffPilotMeasurements">,
    annotation: MeasurementAnnotation
  ) => Promise<void>;
  handleCancelDraft: () => void;
  clearSelection: () => void;
  setInteraction: Dispatch<SetStateAction<InteractionState | null>>;
  setContextMenu: Dispatch<SetStateAction<MeasurementOverlayContextMenuState | null>>;
  setDrawingDraft: Dispatch<SetStateAction<DraftMeasurementState | null>>;
  setPreviewAnnotations: Dispatch<SetStateAction<Record<string, MeasurementAnnotation>>>;
  setPreviewGeometries: Dispatch<
    SetStateAction<Record<string, MeasurementGeometry | PolygonGeometry>>
  >;
  setErrorMessage: Dispatch<SetStateAction<string | null>>;
}

export function useMeasurementOverlayStageHandlers({
  viewport,
  scale,
  activeTool,
  activeMeasurementType,
  snapEnabled,
  incrementSnapEnabled,
  measurements,
  selectedMeasurementIds,
  previewAnnotations,
  previewGeometries,
  interaction,
  interactionRef,
  drawingDraftRef,
  latestDraftRef,
  lastPointerPagePointRef,
  isSpacePressedRef,
  getPagePoint,
  getScreenDistance,
  getActiveHeight,
  snapSegmentPoint,
  snapRectangleCorner,
  updateUnsavedDraft,
  onDraftMeasurementChange,
  onSelectMeasurement,
  onUpdateMeasurement,
  onUpdateMeasurementAnnotation,
  handleCancelDraft,
  clearSelection,
  setInteraction,
  setContextMenu,
  setDrawingDraft,
  setPreviewAnnotations,
  setPreviewGeometries,
  setErrorMessage,
}: UseMeasurementOverlayStageHandlersArgs) {
  const clearInteractionState = useCallback(() => {
    interactionRef.current = null;
    setInteraction(null);
  }, [interactionRef, setInteraction]);

  const finalizeDrawing = useCallback(
    () =>
      finalizeDrawingInteraction({
        drawingDraftRef,
        viewport,
        setErrorMessage,
        setDrawingDraft,
        interactionRef,
        setInteraction,
        latestDraftRef,
        onDraftMeasurementChange,
      }),
    [
      drawingDraftRef,
      interactionRef,
      latestDraftRef,
      onDraftMeasurementChange,
      setDrawingDraft,
      setErrorMessage,
      setInteraction,
      viewport,
    ]
  );

  const handleStageMouseDown = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      const activeInteraction = interactionRef.current ?? interaction;
      if (event.button === 2) {
        return;
      }
      setContextMenu(null);

      if (isSpacePressedRef.current) {
        const nextInteraction: InteractionState = {
          mode: "pan",
          startClientX: event.clientX,
          startClientY: event.clientY,
          initialPanX: viewport.camera.pan.x,
          initialPanY: viewport.camera.pan.y,
          previousInteraction:
            activeInteraction?.mode === "drawing" ? activeInteraction : undefined,
        };
        interactionRef.current = nextInteraction;
        setInteraction(nextInteraction);
        return;
      }

      if (activeInteraction?.mode === "drawing" && activeTool === TOOL_IDS.rectangle) {
        return;
      }

      if (selectedMeasurementIds.length > 0 && !latestDraftRef.current) {
        clearSelection();
        return;
      }

      const handledByDrawingMode = handleDrawingPointerDownInteraction({
        event,
        activeInteraction,
        activeTool,
        activeMeasurementType,
        scale,
        getPagePoint,
        setLastPointerPagePoint: (point) => {
          lastPointerPagePointRef.current = point;
        },
        onSelectMeasurement: (measurementId) => onSelectMeasurement(measurementId),
        handleCancelDraft,
        setErrorMessage,
        interactionRef,
        setInteraction,
        drawingDraftRef,
        setDrawingDraft,
        getActiveHeight,
        getScreenDistance,
        snapEnabled,
        snapSegmentPoint,
        finalizeDrawing,
      });

      if (handledByDrawingMode) {
        return;
      }
    },
    [
      activeMeasurementType,
      activeTool,
      clearSelection,
      drawingDraftRef,
      finalizeDrawing,
      getActiveHeight,
      getPagePoint,
      getScreenDistance,
      handleCancelDraft,
      interaction,
      interactionRef,
      isSpacePressedRef,
      latestDraftRef,
      onSelectMeasurement,
      scale,
      selectedMeasurementIds.length,
      setContextMenu,
      setDrawingDraft,
      setErrorMessage,
      setInteraction,
      snapEnabled,
      snapSegmentPoint,
      viewport.camera.pan.x,
      viewport.camera.pan.y,
      lastPointerPagePointRef,
    ]
  );

  const handleStageContextMenu = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      const pagePoint = getPagePoint(event.clientX, event.clientY);
      if (pagePoint) {
        lastPointerPagePointRef.current = pagePoint;
      }
      const overlayRect = event.currentTarget.getBoundingClientRect();
      handleCancelDraft();
      setContextMenu({
        target: { type: "document" },
        x: event.clientX - overlayRect.left,
        y: event.clientY - overlayRect.top,
        pagePoint,
      });
    },
    [getPagePoint, handleCancelDraft, lastPointerPagePointRef, setContextMenu]
  );

  const handleStageMouseMove = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      const activeInteraction = interactionRef.current ?? interaction;
      const pagePoint = getPagePoint(event.clientX, event.clientY);
      if (!pagePoint) {
        return;
      }
      lastPointerPagePointRef.current = pagePoint;

      if (!activeInteraction) {
        return;
      }

      if (
        applyTransformPreviewOnPointerMove({
          activeInteraction,
          event,
          pagePoint,
          viewport,
          measurements,
          incrementSnapEnabled,
          scale,
          snapSegmentPoint,
          snapRectangleCorner,
          setPreviewGeometries,
          setPreviewAnnotations,
          updateUnsavedDraft,
        })
      ) {
        return;
      }

      if (activeInteraction.mode === "drawing") {
        updateDrawingPreviewInteraction({
          activeInteraction,
          activeTool,
          activeMeasurementType,
          snapEnabled,
          getActiveHeight,
          scale,
          snapSegmentPoint,
          snapRectangleCorner,
          pagePoint,
          shiftKey: event.shiftKey,
          drawingDraftRef,
          setDrawingDraft,
        });
      }
    },
    [
      interaction,
      interactionRef,
      getPagePoint,
      lastPointerPagePointRef,
      viewport,
      measurements,
      incrementSnapEnabled,
      scale,
      snapSegmentPoint,
      snapRectangleCorner,
      setPreviewGeometries,
      updateUnsavedDraft,
      activeTool,
      activeMeasurementType,
      snapEnabled,
      getActiveHeight,
      drawingDraftRef,
      setDrawingDraft,
    ]
  );

  const handleStageMouseUp = useCallback(async () => {
    const activeInteraction = interactionRef.current ?? interaction;
    if (!activeInteraction) {
      return;
    }

    if (activeInteraction.mode === "drawing" && activeTool === TOOL_IDS.rectangle) {
      try {
        await finalizeDrawing();
      } catch (error) {
        console.error("Failed to finalize rectangle drawing:", error);
        setErrorMessage("Failed to finalize measurement");
      } finally {
        clearInteractionState();
      }
      return;
    }

    if (activeInteraction.mode === "drawing") {
      return;
    }

    if (activeInteraction.mode === "pan") {
      if (activeInteraction.previousInteraction) {
        interactionRef.current = activeInteraction.previousInteraction;
        setInteraction(activeInteraction.previousInteraction);
      } else {
        clearInteractionState();
      }
      return;
    }

    if (activeInteraction.mode === "move-draft") {
      clearInteractionState();
      return;
    }

    if (activeInteraction.mode === "move-label") {
      const nextAnnotation = previewAnnotations[String(activeInteraction.measurementId)];
      try {
        if (nextAnnotation && onUpdateMeasurementAnnotation) {
          await onUpdateMeasurementAnnotation(activeInteraction.measurementId, nextAnnotation);
        }
      } catch (error) {
        console.error("Failed to commit preview measurement annotation updates:", error);
        setErrorMessage("Failed to update measurement label");
      } finally {
        clearInteractionState();
        setPreviewAnnotations({});
      }
      return;
    }

    let canClearPreview = false;
    try {
      canClearPreview = await commitPreviewMeasurementUpdates({
        previewGeometries,
        measurements,
        scale,
        onUpdateMeasurement,
      });
    } catch (error) {
      console.error("Failed to commit preview measurement updates:", error);
      setErrorMessage("Failed to update measurement");
    } finally {
      clearInteractionState();
    }

    if (canClearPreview) {
      setPreviewGeometries({});
    }
  }, [
    activeTool,
    clearInteractionState,
    finalizeDrawing,
    interaction,
    measurements,
    onUpdateMeasurementAnnotation,
    onUpdateMeasurement,
    previewAnnotations,
    previewGeometries,
    scale,
    setErrorMessage,
    setPreviewAnnotations,
    setPreviewGeometries,
  ]);

  const handleStageMouseLeave = useCallback(() => {
    if (!interactionRef.current && !interaction) {
      return;
    }
    void handleStageMouseUp();
  }, [handleStageMouseUp, interaction, interactionRef]);

  return {
    handleStageMouseDown,
    handleStageContextMenu,
    handleStageMouseMove,
    handleStageMouseUp,
    handleStageMouseLeave,
    finalizeDrawing,
  };
}

export type { UseMeasurementOverlayStageHandlersArgs };
